// Tastenkürzel: Listen (j/k/Enter), Lead-Seite (j/k nächster/vorheriger Lead), global (?, /, Cmd/Strg+K).
import { useEffect, useRef } from "react";

/** Tippt die Person gerade in ein Eingabefeld? Dann gelten Einzeltasten nicht. */
export function tipptGerade(e: KeyboardEvent): boolean {
  const z = e.target as HTMLElement | null;
  if (!z || !z.tagName) return false;
  const tag = z.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || z.isContentEditable;
}

// Offene Ebenen (Befehlspalette, Dialoge, Aufklappmenüs): solange eine offen ist, gelten Seitenkürzel nicht.
let ebenen = 0;
export const ebene = {
  auf(): void {
    ebenen += 1;
  },
  zu(): void {
    ebenen = Math.max(0, ebenen - 1);
  },
  offen(): boolean {
    return ebenen > 0;
  },
};

/** Taste als Text: „j“, „Enter“, „mod+Enter“ (mod = Cmd auf dem Mac, Strg sonst), „shift+ArrowDown“. */
export function tastenName(e: KeyboardEvent): string {
  const teile: string[] = [];
  if (e.metaKey || e.ctrlKey) teile.push("mod");
  if (e.altKey) teile.push("alt");
  if (e.shiftKey && e.key.length > 1) teile.push("shift");
  teile.push(e.key.length === 1 ? e.key.toLowerCase() : e.key);
  return teile.join("+");
}

type Belegung = Record<string, (e: KeyboardEvent) => void>;

/**
 * Seitenkürzel. Einzeltasten wirken nicht beim Tippen und nicht, wenn eine Ebene offen ist.
 * Kürzel mit „mod+“ wirken auch beim Tippen.
 */
export function useTasten(belegung: Belegung, aktiv = true): void {
  const ref = useRef(belegung);
  ref.current = belegung;
  useEffect(() => {
    if (!aktiv) return;
    const handler = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      const name = tastenName(e);
      const fn = ref.current[name];
      if (!fn) return;
      const mitMod = name.startsWith("mod+");
      if (!mitMod && (tipptGerade(e) || ebene.offen())) return;
      if (mitMod && ebene.offen()) return;
      e.preventDefault();
      fn(e);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [aktiv]);
}

export const istMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
export const modTaste = istMac ? "⌘" : "Strg";

/** Text in die Zwischenablage. */
export async function kopieren(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const feld = document.createElement("textarea");
    feld.value = text;
    feld.style.position = "fixed";
    feld.style.opacity = "0";
    document.body.appendChild(feld);
    feld.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      ok = false;
    }
    feld.remove();
    return ok;
  }
}
